import api from '../lib/api';
import type { Lead, LeadFormData, LeadFilters } from '../types/lead';

interface PaginatedResponse {
  data: Lead[];
  pagination: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
}

export const leadService = {
  async getAll(page = 1, limit = 20, filters?: LeadFilters): Promise<PaginatedResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters?.search) {
      params.append('search', filters.search);
    }

    if (filters?.representative_id) {
      params.append('representative_id', filters.representative_id.toString());
    }

    if (filters?.origin) {
      params.append('origin', filters.origin);
    }

    if (filters?.has_email !== undefined) {
      params.append('has_email', filters.has_email.toString());
    }

    if (filters?.has_phone !== undefined) {
      params.append('has_phone', filters.has_phone.toString());
    }

    if (filters?.lead_group_id) {
      params.append('lead_group_id', filters.lead_group_id.toString());
    }

    if (filters?.last_contact_from) {
      params.append('last_contact_from', filters.last_contact_from);
    }

    if (filters?.last_contact_to) {
      params.append('last_contact_to', filters.last_contact_to);
    }

    if (filters?.type) {
      params.append('type', filters.type);
    }

    const response = await api.get(`/mkt-leads?${params.toString()}`);
    return response.data;
  },

  async getById(id: number): Promise<Lead> {
    const response = await api.get(`/mkt-leads/${id}`);
    return response.data;
  },

  async create(data: LeadFormData): Promise<Lead> {
    const response = await api.post('/mkt-leads', data);
    return response.data;
  },

  async update(id: number, data: LeadFormData): Promise<Lead> {
    const response = await api.put(`/mkt-leads/${id}`, data);
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/mkt-leads/${id}`);
  },

  async updateLastContact(id: number, lastContactAt?: string): Promise<Lead> {
    const response = await api.patch(`/mkt-leads/${id}/last-contact`, {
      last_contact_at: lastContactAt,
    });
    return response.data;
  },

  async addToGroup(id: number, leadGroupId: number): Promise<Lead> {
    const response = await api.patch(`/mkt-leads/${id}/groups/add`, {
      lead_group_id: leadGroupId,
    });
    return response.data;
  },

  async removeFromGroup(id: number, leadGroupId: number): Promise<Lead> {
    const response = await api.patch(`/mkt-leads/${id}/groups/remove`, {
      lead_group_id: leadGroupId,
    });
    return response.data;
  },
};
